import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../utils/cn.js';

export interface DrawerProps {
  open: boolean;
  onOpenChange?: (o: boolean) => void;
  /** Backward-compat alias — fires with `false` when the drawer wants to close. */
  onClose?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Fixed-height sticky footer for actions. */
  footer?: ReactNode;
  /** Width preset. `sm` 400, `md` (default) 480, `lg` 640, `xl` 800. */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZE: Record<NonNullable<DrawerProps['size']>, string> = {
  sm: 'drawer-sm',
  md: 'drawer-md',
  lg: 'drawer-lg',
  xl: 'drawer-xl',
};

/**
 * Right-side slide-in drawer built on Radix Dialog for a11y (focus trap, escape close, aria).
 * Kollez rule: drawers over modals for routine work. Modals reserved for irreversible actions.
 */
export function Drawer({
  open,
  onOpenChange,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: DrawerProps) {
  const handleOpenChange = (o: boolean) => {
    if (onOpenChange) onOpenChange(o);
    if (!o && onClose) onClose();
  };
  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className={cn('drawer', SIZE[size], className)}>
          {title || description ? (
            <div className="drawer-head">
              <div>
                {title ? <Dialog.Title className="drawer-title">{title}</Dialog.Title> : null}
                {description ? (
                  <Dialog.Description className="drawer-desc">{description}</Dialog.Description>
                ) : null}
              </div>
              <Dialog.Close className="drawer-close" aria-label="Close">
                <X size={16} />
              </Dialog.Close>
            </div>
          ) : null}
          <div className="drawer-body">{children}</div>
          {footer ? <div className="drawer-foot">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
